import { useEffect, useRef } from "react";

import { getSupabaseClient } from "@/app/_module/services/supabaseClient";
import { removeRealtimeChannel } from "@/app/_module/services/realtimeTeardown";
import { useRealtimeRejoin } from "@/app/_module/services/realtimeHealth";

export interface SalePulse {
  leadId?: string;
  agentName?: string;
  carrier?: string;
  /**
   * Whole dollars. The DB stores premium in CENTS (both write paths do
   * `Math.round(annualPremium * 100)`); the backend converts before broadcasting, and the
   * field is named for its unit because an early version of the toast rendered a $5,354
   * policy as "$535,400".
   *
   * Client name and policy type are deliberately absent — `postSalesWin` strips both
   * from the chat card, and a public broadcast channel is no place to reinstate them.
   */
  policyAmountDollars?: number;
}

/**
 * Fires when someone in the viewer's organisation closes a deal.
 *
 * Listens on `agency-sales:{viewerAgencyId}`. The backend fans a sale out to the
 * seller's agency AND every ancestor above it (`broadcastSaleWonUpTree`), which is the
 * same fan-out that fixed upline owners missing sub-agency live calls — so an owner at
 * the top of a deep tree hears about a sale five levels down, and a multi-agency seller
 * is announced to all of their memberships.
 *
 * ⚠️ NOT a `postgres_changes` subscription on `team_chat_messages`, which is the obvious
 * shortcut since every sale already writes a `sales_win` row there. Two reasons:
 *   1. It is dead — that table has RLS on with zero policies and no `authenticated`
 *      SELECT grant, so Realtime delivers nothing (this is why team chat polls).
 *   2. A table subscription can't be scoped to the viewer's org, so every user would
 *      receive every other agency's agent names, client names and premium amounts.
 *      Scoping by broadcast topic makes that leak structurally impossible.
 *
 * The topic is distinct from `agency-live-calls:{id}`: supabase-js hands back the SAME
 * channel object per topic, so sharing one would register handlers on an already-
 * subscribed channel and throw post-2.101.
 */
export function useSalePulse(agencyId: string | undefined, onSale: (sale: SalePulse) => void) {
  const { rejoinToken, handleStatus } = useRealtimeRejoin("hierarchy-sale-pulse");

  // Held in a ref so a caller passing an inline arrow doesn't tear the channel down and
  // rebuild it on every render.
  const handler = useRef(onSale);
  handler.current = onSale;

  useEffect(() => {
    if (!agencyId) return;

    let channel: any = null;
    let cancelled = false;

    void getSupabaseClient().then((supabase) => {
      if (cancelled || !supabase) return;
      channel = supabase.channel(`agency-sales:${agencyId}`);
      channel.on("broadcast", { event: "sale_won" }, (msg: any) => {
        handler.current(msg?.payload ?? {});
      });
      channel.subscribe(handleStatus);
    });

    return () => {
      cancelled = true;
      removeRealtimeChannel(channel);
    };
  }, [agencyId, handleStatus, rejoinToken]);
}
